import { Button } from "risa-oasis-ui_v2";
import InactiveWarningIcon from "../../../../svg/inactiveWarningIcon";
import { InfoIcon } from "../../../../svg/info-icon";

/** Visual variants for EV coverage empty-state panels. */
export type EvCoverageEmptyStateVariant =
  | "noCoverageData"
  | "noPatientResponsibility"
  | "tierUnavailable"
  | "medicaidDetailsUnavailable";

export interface EvCoverageEmptyStateProps {
  /** Selects title, description, and icon treatment for the empty state. */
  variant: EvCoverageEmptyStateVariant;
  /** Optional extra class names for the outer bordered container. */
  className?: string;
  /**
   * When provided, renders a Refresh action below the description that runs the
   * same coverage refresh as the header icon.
   */
  onRefreshClick?: () => void;
  /** True while a coverage refresh is in flight; disables the Refresh action. */
  isRefreshing?: boolean;
}

const EMPTY_STATE_COPY: Record<
  EvCoverageEmptyStateVariant,
  { title: string; description: string; useWarningIcon: boolean }
> = {
  noCoverageData: {
    title: "No Coverage Information Available",
    description:
      "Coverage details have not been retrieved for this order yet. Use Refresh to try again, or verify coverage directly through the payer portal.",
    useWarningIcon: true,
  },
  noPatientResponsibility: {
    title: "Benefit Verification Details Unavailable",
    description:
      "Treatment-level benefit verification values are not available for this plan. Use Edit on the plan summary to add values manually.",
    useWarningIcon: false,
  },
  tierUnavailable: {
    title: "Coverage Details Unavailable",
    description:
      "Detailed coverage information is not available for this insurance tier. Verify the plan in the payer portal or try refreshing coverage.",
    useWarningIcon: false,
  },
  medicaidDetailsUnavailable: {
    title: "Medicaid Details Unavailable",
    description:
      "Coverage details were not retrieved for this plan. Please verify coverage directly through the payer portal.",
    useWarningIcon: false,
  },
};

/**
 * Centered empty-state panel for EV coverage when data is missing or incomplete.
 * Matches the visual language of {@link InactiveInsurance} message-only variants.
 *
 * @param props - Variant, optional layout classes, and optional refresh action
 * @returns Bordered empty-state panel for the coverage column
 */
export const EvCoverageEmptyState = (props: EvCoverageEmptyStateProps) => {
  const {
    variant,
    className = "",
    onRefreshClick,
    isRefreshing = false,
  } = props;
  const copy = EMPTY_STATE_COPY[variant];

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col items-center justify-center overflow-auto rounded-lg border border-primaryGray-15 bg-white p-4 sm:p-6 ${className}`.trim()}
    >
      <div className="flex w-full max-w-lg flex-col items-center justify-center gap-4 px-2 py-4 sm:gap-5 sm:py-6">
        {copy.useWarningIcon ? (
          <div className="icon--container relative flex items-center justify-center">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.35]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgb(214 214 214 / 0.45) 1px, transparent 1px), linear-gradient(to bottom, rgb(214 214 214 / 0.45) 1px, transparent 1px)",
                backgroundSize: "10px 10px",
              }}
              aria-hidden
            />
            <InactiveWarningIcon width="12rem" height="12rem" />
          </div>
        ) : (
          <div
            className="relative flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-sm border border-[#2E2E2E] bg-white shadow-[4px_4px_0_0_#2E2E2E]"
            aria-hidden
          >
            <InfoIcon
              height="28"
              width="28"
              stroke="#0056D6"
              strokeWidth="1.5"
            />
          </div>
        )}
        <div className="flex flex-col items-center gap-2 text-center sm:gap-3">
          <p className="text-small font-bold leading-5 text-primaryGray-1 sm:text-h11 sm:font-semibold">
            {copy.title}
          </p>
          <p className="text-small font-normal leading-relaxed text-primaryGray-3">
            {copy.description}
          </p>
        </div>
        {onRefreshClick && (
          <Button
            buttonType="primary"
            size="medium"
            disabled={isRefreshing}
            onClick={onRefreshClick}
          >
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EvCoverageEmptyState;
